import { useState } from "react";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";

type LinkedAdmin = {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
};

type EmployeeWithJms = {
  id: number;
  phone?: string;
  jmsLogin?: { linked: boolean; admin: LinkedAdmin | null };
};

function apiErrorMessage(err: unknown, fallback: string): string {
  const body = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data;
  const msg = body?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}

/**
 * Staff Edit section: show JMS login link status and invite / unlink actions.
 */
export function StaffJmsLoginSection() {
  const record = useRecordContext<EmployeeWithJms>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [busy, setBusy] = useState(false);

  if (!record) return null;

  const linked = record.jmsLogin?.linked === true;
  const admin = record.jmsLogin?.admin ?? null;

  const openInvite = () => {
    setEmail("");
    setPhoneNumber(record.phone?.startsWith("+") ? record.phone : "");
    setInviteOpen(true);
  };

  const handleInvite = async () => {
    setBusy(true);
    try {
      const payload: { email?: string; phoneNumber?: string } = {};
      const trimmedEmail = email.trim();
      const trimmedPhone = phoneNumber.trim();
      if (trimmedEmail) payload.email = trimmedEmail;
      if (trimmedPhone) payload.phoneNumber = trimmedPhone;
      await callApi({
        location: `employees/${record.id}/jms-login`,
        method: "POST",
        data: payload,
      });
      notify("JMS login linked", { type: "success" });
      setInviteOpen(false);
      refresh();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to enable JMS login"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm("Unlink this JMS login from the employee? The admin account is kept.")) {
      return;
    }
    setBusy(true);
    try {
      await callApi({
        location: `employees/${record.id}/jms-login`,
        method: "DELETE",
      });
      notify("JMS login unlinked", { type: "success" });
      refresh();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to unlink JMS login"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        JMS login (My dashboard)
      </Typography>
      {linked && admin ? (
        <Stack spacing={1.5}>
          <Alert severity="success" variant="outlined">
            Linked to {admin.email}
            {admin.phoneNumber ? ` · ${admin.phoneNumber}` : ""}
          </Alert>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => void handleUnlink()}
            disabled={busy}
            sx={{ alignSelf: "flex-start" }}
          >
            Unlink
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Not linked. Invite creates an Admin login with only My dashboard access.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={openInvite}
            disabled={busy}
            sx={{ alignSelf: "flex-start" }}
          >
            Enable JMS login
          </Button>
        </Stack>
      )}

      <Dialog
        open={inviteOpen}
        onClose={() => !busy && setInviteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Enable JMS login</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Provide email and/or E.164 phone so the staff member can sign in. At least one channel
              is required if the employee phone cannot be normalized.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone (E.164)"
              placeholder="+919876543210"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              size="small"
              helperText="Optional if employee phone is a valid 10-digit Indian number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleInvite()} disabled={busy}>
            Invite / link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
